import Phaser from 'phaser';
import { GAME_FONT_FAMILY } from '@shared/constants';
import {
  ClientMessageType,
  type PartySyncMessage,
  type PartyInviteReceivedMessage,
  type PartyFinderResultsMessage,
  type PartyNotifyMessage,
} from '@shared/types/messages';
import type {
  PartyView,
  PartyDistributionPolicy,
  PartyQuestPolicy,
  PartyVisibility,
} from '@shared/data/party';
import type { ColyseusClient } from '../../network/ColyseusClient';
import { PartyPanel } from '../ui/PartyPanel';
import { PartyFinderPanel } from '../ui/PartyFinderPanel';

/** Notifica al jugador por el canal de sistema (lo cablea GameScene con el chat). */
type NotifyFn = (text: string, isError: boolean) => void;

/**
 * PartyController — orquesta la UI de party del cliente: la ventana de gestión
 * (PartyPanel), el buscador de grupos (PartyFinderPanel), las invitaciones
 * entrantes y el envío de acciones al servidor. Ver docs/PARTY_SYSTEM.md.
 */
export class PartyController {
  private readonly scene: Phaser.Scene;
  private readonly client: ColyseusClient;
  private readonly notify: NotifyFn;
  private readonly localSessionId: string;

  private panel: PartyPanel;
  private finder: PartyFinderPanel;
  private view: PartyView | null = null;

  private inviteToasts: Phaser.GameObjects.Container[] = [];

  constructor(scene: Phaser.Scene, client: ColyseusClient, localSessionId: string, notify: NotifyFn) {
    this.scene = scene;
    this.client = client;
    this.localSessionId = localSessionId;
    this.notify = notify;

    this.panel = new PartyPanel(
      scene,
      {
        onKick: (sid) => this.send({ type: ClientMessageType.PARTY_KICK, targetSessionId: sid }),
        onTransferLeader: (sid) =>
          this.send({ type: ClientMessageType.PARTY_TRANSFER_LEADER, targetSessionId: sid }),
        onReorder: (order) => this.send({ type: ClientMessageType.PARTY_REORDER, order }),
        onSetPolicy: (p) => this.setPolicy(p),
        onSetXpShares: (shares) => this.send({ type: ClientMessageType.PARTY_SET_XP_SHARES, shares }),
        onLeave: () => this.send({ type: ClientMessageType.PARTY_LEAVE }),
        onDisband: () => this.send({ type: ClientMessageType.PARTY_DISBAND }),
      },
      20,
      120
    );
    this.panel.setLocalSessionId(localSessionId);

    this.finder = new PartyFinderPanel(
      scene,
      {
        onCreate: (visibility, minLevelTag) =>
          this.send({ type: ClientMessageType.PARTY_FINDER_CREATE, visibility, minLevelTag }),
        onSetVisibility: (visibility, minLevelTag) =>
          this.send({ type: ClientMessageType.PARTY_FINDER_SET_VISIBILITY, visibility, minLevelTag }),
        onJoin: (partyId) => this.send({ type: ClientMessageType.PARTY_FINDER_JOIN, partyId }),
        onRefresh: () => this.send({ type: ClientMessageType.PARTY_FINDER_LIST }),
      },
      160,
      100
    );
  }

  // === Mensajes del servidor ===============================================

  onSync(msg: PartySyncMessage): void {
    this.view = msg.party;
    this.panel.update(msg.party);
    this.finder.setParty(msg.party);
  }

  onInviteReceived(msg: PartyInviteReceivedMessage): void {
    this.showInviteToast(msg.fromSessionId, msg.fromName);
  }

  onFinderResults(msg: PartyFinderResultsMessage): void {
    this.finder.setResults(msg.parties);
    this.finder.open();
  }

  onNotify(msg: PartyNotifyMessage): void {
    this.notify(msg.text, !!msg.isError);
  }

  // === Acciones locales =====================================================

  /** Abre/cierra la ventana del party (hotkey). Pide un sync fresco al abrir. */
  togglePanel(): void {
    if (!this.panel.isVisible()) {
      this.send({ type: ClientMessageType.REQUEST_PARTY });
    }
    this.panel.toggle();
  }

  /** Invita a un jugador (desde el menú contextual de click-derecho). */
  invite(targetSessionId: string): void {
    if (targetSessionId === this.localSessionId) return;
    this.send({ type: ClientMessageType.PARTY_INVITE, targetSessionId });
  }

  /** True si el sessionId es miembro del propio party (para colorear su nombre). */
  isPartyMember(sessionId: string): boolean {
    return !!this.view && this.view.members.some((m) => m.sessionId === sessionId);
  }

  isInParty(): boolean {
    return !!this.view;
  }

  private setPolicy(p: {
    goldPolicy?: PartyDistributionPolicy;
    lootPolicy?: PartyDistributionPolicy;
    questPolicy?: PartyQuestPolicy;
  }): void {
    this.send({
      type: ClientMessageType.PARTY_SET_POLICY,
      goldPolicy: p.goldPolicy,
      lootPolicy: p.lootPolicy,
      questPolicy: p.questPolicy,
    });
  }

  // === Invitación (toast) ===================================================

  private showInviteToast(fromSessionId: string, fromName: string): void {
    const W = 300;
    const H = 60;
    const x = this.scene.scale.width / 2 - W / 2;
    const y = 90 + this.inviteToasts.length * (H + 8);
    const c = this.scene.add.container(x, y).setDepth(1060).setScrollFactor(0);
    const bg = this.scene.add.rectangle(0, 0, W, H, 0x1d1810).setOrigin(0, 0);
    bg.setStrokeStyle(1, 0xc79a3e, 0.7);
    const txt = this.scene.add.text(
      10,
      8,
      `${fromName} te invitó al grupo`,
      { fontFamily: GAME_FONT_FAMILY, fontSize: '13px', color: '#e8dcb0', resolution: 2 }
    );
    const accept = this.mkBtn(10, 30, 130, 'Aceptar', () => {
      this.send({ type: ClientMessageType.PARTY_INVITE_RESPONSE, fromSessionId, accept: true });
      this.removeToast(c);
    });
    const reject = this.mkBtn(150, 30, 130, 'Rechazar', () => {
      this.send({ type: ClientMessageType.PARTY_INVITE_RESPONSE, fromSessionId, accept: false });
      this.removeToast(c);
    });
    c.add([bg, txt, ...accept, ...reject]);
    this.inviteToasts.push(c);

    // Auto-descartar (rechazar implícito) a los 30s.
    this.scene.time.delayedCall(30000, () => this.removeToast(c));
  }

  private mkBtn(x: number, y: number, w: number, label: string, onClick: () => void): Phaser.GameObjects.GameObject[] {
    const bg = this.scene.add.rectangle(x, y, w, 22, 0x2a2210).setOrigin(0, 0);
    bg.setStrokeStyle(1, 0xc79a3e, 0.5);
    bg.setInteractive({ useHandCursor: true });
    const t = this.scene.add
      .text(x + w / 2, y + 11, label, { fontFamily: GAME_FONT_FAMILY, fontSize: '12px', color: '#e8dcb0', resolution: 2 })
      .setOrigin(0.5, 0.5);
    bg.on('pointerdown', onClick);
    return [bg, t];
  }

  private removeToast(c: Phaser.GameObjects.Container): void {
    const i = this.inviteToasts.indexOf(c);
    if (i === -1) return;
    this.inviteToasts.splice(i, 1);
    c.destroy(true);
    // Recolocar los restantes.
    this.inviteToasts.forEach((t, idx) => t.setY(90 + idx * 68));
  }

  private send(message: Parameters<ColyseusClient['send']>[0]): void {
    this.client.send(message);
  }

  destroy(): void {
    this.panel.destroy();
    this.finder.destroy();
    this.inviteToasts.forEach((t) => t.destroy(true));
    this.inviteToasts = [];
  }
}
